import ActionButton from "components/Buttons/ActionButton";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import ThemeTabs from "components/Tabs/ThemeTabs";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Quotation from "./Quotation";
import QuotationRequest from "./QuotationRequest";

const QuotationIndex: React.FC = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const prevTabRef = useRef<number | null>(null);

  // Use tab numbers: 1 = Quotation Request, 2 = Quotation
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("QuotationTab")) || 1
  );

  // 🔥 Use preserved table state hook with tabIndex for per-tab state preservation
  const tableState = usePreservedTableState({
    key: "quotation",
    tabIndex: tab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection: "" | "asc" | "desc" | undefined = sorting[0]?.desc
    ? "desc"
    : sorting[0]
    ? "asc"
    : undefined;

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.order?.app_name,
    app_modules?.order?.modules?.quotation
  );

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // State for tab header handlers
  const [quotationRequestHandlers, setQuotationRequestHandlers] = useState<{
    searchInput: string;
    setSearchInput: (value: string) => void;
    handleExport: () => void;
  } | null>(null);

  const [quotationHandlers, setQuotationHandlers] = useState<{
    searchInput: string;
    setSearchInput: (value: string) => void;
    handleExport: () => void;
  } | null>(null);

  // ✅ Tab switch - reset state when tab changes
  const handleTabChange = (val: number) => {
    setTab(val);
    sessionStorage.setItem("QuotationTab", val.toString());
  };

  // Reset state when tab changes (using useEffect to handle tab changes)
  // This ensures state is cleared when switching tabs, but preserved when navigating away and back
  useEffect(() => {
    // Only reset if tab actually changed (not on initial mount)
    // The hook will have re-initialized with the new tabIndex by this point
    if (prevTabRef.current !== null && prevTabRef.current !== tab) {
      // Clear the state for the new tab
      resetState();
    }
    prevTabRef.current = tab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]); // Only depend on tab, not resetState to avoid infinite loops

  // Callback to receive handlers from QuotationRequest component
  const handleQuotationRequestHeaderReady = useCallback(
    (handlers: {
      searchInput: string;
      setSearchInput: (value: string) => void;
      handleExport: () => void;
    }) => {
      setQuotationRequestHandlers(handlers);
    },
    []
  );

  // Callback to receive handlers from Quotation component
  const handleQuotationHeaderReady = useCallback(
    (handlers: {
      searchInput: string;
      setSearchInput: (value: string) => void;
      handleExport: () => void;
    }) => {
      setQuotationHandlers(handlers);
    },
    []
  );

  // Reset handlers when tab changes
  useEffect(() => {
    if (tab !== 1) {
      setQuotationRequestHandlers(null);
    }
    if (tab !== 2) {
      setQuotationHandlers(null);
    }
  }, [tab]);

  // Tabs configuration
  const tabs = [{ label: "Quotation Request" }, { label: "Quotation" }];

  return (
    <ListingPageContainer>
      <Card>
        <Card.Header>
          {/* Header - common for both tabs */}
          {((tab === 1 && quotationRequestHandlers) ||
            (tab === 2 && quotationHandlers)) && (
            <Row className="align-items-center g-2 mb-3">
              <Col lg={3} className="me-auto">
                <h6 className="card-title mb-0">Quotation</h6>
              </Col>

              {/* Search Input Box */}
              <Col xl={2} md={3}>
                <SearchBox
                  value={
                    tab === 1
                      ? quotationRequestHandlers?.searchInput || ""
                      : quotationHandlers?.searchInput || ""
                  }
                  onChange={
                    tab === 1
                      ? quotationRequestHandlers?.setSearchInput || (() => {})
                      : quotationHandlers?.setSearchInput || (() => {})
                  }
                />
              </Col>

              <Col className="col-md-auto">
                <div className="hstack gap-2">
                  <ActionButton
                    label="Export"
                    icon="bi bi-box-arrow-up"
                    handleClick={
                      tab === 1
                        ? quotationRequestHandlers?.handleExport || (() => {})
                        : quotationHandlers?.handleExport || (() => {})
                    }
                    variant="light"
                  />

                  {/* Create Quotation button - only for Quotation tab */}
                  {tab === 2 && canCreate && (
                    <ActionButton
                      label="Create Quotation"
                      icon="bi bi-plus-circle"
                      navigateLink="/quotation/create"
                      variant="primary"
                    />
                  )}
                </div>
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            {/* Tabs */}
            <Col xs={12}>
              <ThemeTabs
                tabs={tabs}
                value={tab - 1}
                onChange={(newValue) => handleTabChange(newValue + 1)}
              />
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {tab === 1 && (
            <QuotationRequest
              pageIndex={pageIndex}
              pageSize={pageSize}
              sorting={sorting}
              columnFilters={columnFilters}
              filterValue={filterValue}
              search={search}
              searchInput={searchInput}
              sortField={sortField}
              sortDirection={sortDirection}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              setSorting={setSorting}
              setColumnFilters={setColumnFilters}
              setFilterValue={setFilterValue}
              setSearch={setSearch}
              setSearchInput={setSearchInput}
              onHeaderReady={handleQuotationRequestHeaderReady}
            />
          )}
          {tab === 2 && (
            <Quotation
              pageIndex={pageIndex}
              pageSize={pageSize}
              sorting={sorting}
              columnFilters={columnFilters}
              filterValue={filterValue}
              search={search}
              searchInput={searchInput}
              sortField={sortField}
              sortDirection={sortDirection}
              setPageIndex={setPageIndex}
              setPageSize={setPageSize}
              setSorting={setSorting}
              setColumnFilters={setColumnFilters}
              setFilterValue={setFilterValue}
              setSearch={setSearch}
              setSearchInput={setSearchInput}
              onHeaderReady={handleQuotationHeaderReady}
            />
          )}
        </Card.Body>
      </Card>
    </ListingPageContainer>
  );
};

export default QuotationIndex;
